import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    ContentCollectionContract,
    LinkedContentObjectsContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Collection } from 'src/app/models/collection';
import { ContentCollection } from 'src/app/models/content-collection';
import {
    ForceCollectionRefresh,
    RemoveForceCollectionContentRefresh,
} from '../components/common/collection/collection.actions';
import { selectShouldForceCollectionContentRefreshList } from '../components/common/collection/collection.reducer';
import { AppState } from '../models/app-state';

@Injectable({ providedIn: 'root' })
export class CollectionResourceService {
    private collectionsUrl: string;
    private collectionCache: Record<
        string,
        ContentCollectionContract | undefined
    > = {};
    private collectionContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        private store: Store<AppState>,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.collectionsUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'collections'
        );

        this.store
            .select(selectShouldForceCollectionContentRefreshList)
            .subscribe((shouldForceRefreshList) => {
                if (shouldForceRefreshList.length > 0) {
                    shouldForceRefreshList.forEach((contentId) => {
                        this.removeCacheByContent(contentId);
                    });
                    this.store.dispatch(
                        new RemoveForceCollectionContentRefresh()
                    );
                }
            });
    }

    getCollections(shouldForce = false): Observable<Collection[]> {
        return this.http
            .get<
                ContentCollectionContract[]
            >(`${this.collectionsUrl}?includeDeleted=false`)
            .pipe(
                map((contracts) => {
                    return contracts.map((contract) => {
                        return new Collection(
                            contract,
                            this.getCollectionContents(
                                contract.id || '',
                                shouldForce
                            )
                        );
                    });
                })
            );
    }

    getCollectionById(collectionId: string): Observable<Collection> {
        const cachedContract = this.collectionCache[collectionId];
        if (cachedContract) {
            return of(new Collection(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.collectionsUrl,
            collectionId
        );
        return this.http.get<ContentCollectionContract>(url).pipe(
            tap(
                (contract) =>
                    (this.collectionCache[collectionId] = cloneDeep(contract))
            ),
            map(
                (contract) =>
                    new Collection(
                        contract,
                        this.getCollectionContents(contract.id || '')
                    )
            )
        );
    }

    getCollectionContents(
        collectionId: string,
        shouldForce = false
    ): Observable<ContentCollection> {
        const cachedContract = this.collectionContentCache[collectionId];

        const url = this.routingHelpersService.combineUrl(
            this.collectionsUrl,
            collectionId,
            'contents'
        );
        if (shouldForce || !cachedContract) {
            return this.http.get<LinkedContentObjectsContract>(url).pipe(
                tap((contract) => {
                    this.collectionContentCache[collectionId] =
                        cloneDeep(contract);
                }),
                map((contract) => new ContentCollection(contract))
            );
        } else {
            return of(new ContentCollection(cachedContract));
        }
    }

    updateCollection(collection: Collection): Observable<void> {
        this.removeCaches(collection.id);
        const url = this.routingHelpersService.combineUrl(
            this.collectionsUrl,
            collection.id
        );

        return this.http.put<undefined>(url, collection.toContract());
    }

    createCollection(collection: Collection): Observable<Collection> {
        return this.http
            .post<{ id: string }>(this.collectionsUrl, collection.toContract())
            .pipe(
                map((response) => {
                    const newCollection = collection.clone();
                    newCollection.id = response.id;
                    return newCollection;
                })
            );
    }

    deleteCollection(collectionId: string): Observable<void> {
        this.removeCaches(collectionId);
        const url = this.routingHelpersService.combineUrl(
            this.collectionsUrl,
            collectionId
        );

        return this.http.delete<undefined>(url);
    }

    private removeCaches(collectionId: string): void {
        this.collectionCache[collectionId] = undefined;
        this.collectionContentCache[collectionId] = undefined;
    }

    private removeCacheByContent(contentId: string): void {
        const collectionList: string[] = [];

        for (const key in this.collectionContentCache) {
            if (this.collectionContentCache[key]) {
                for (const subCollection of Object.values(
                    this.collectionContentCache[key]
                )) {
                    if (
                        Array.isArray(subCollection) &&
                        subCollection.some(
                            (item) =>
                                item.id === contentId ||
                                item.properties?.id === contentId
                        )
                    ) {
                        collectionList.push(key);
                    }
                }
            }
        }

        collectionList.forEach((collectionId) => {
            if (this.collectionContentCache[collectionId] !== undefined) {
                this.collectionContentCache[collectionId] = undefined;
                this.store.dispatch(new ForceCollectionRefresh(true));
            }
        });
    }
}
